import { useFormik } from "formik";
import { observer } from "mobx-react";
import type React from "react";
import { Trans, useTranslation } from "react-i18next";

import { TeacherModals } from "constants/modals-constants";

import { useDomainHandler } from "hooks/useDomainHandler";
import { useGroups } from "hooks/useGroups";
import { useModal } from "hooks/useModal";

import { ModalRenderType } from "components/Modal/Modal.model";
import { MultipleEmailInput } from "components/MultipleEmailInput/MultipleEmailInput";

import { Heading2 } from "styles/elements/Headings";
import { ParagraphLarge } from "styles/elements/Texts";
import { CssButton, CssFlex } from "styles/helpers/layout";

import { Note } from "components/Note/Note";
import {
  type AddStudentsToGroupFormFields,
  buildAddStudentsToGroupFormSchema,
} from "./AddStudentsToGroupModal.schema";
import { StyledModal } from "./AddStudentsToGroupModal.styled";

interface AddStudentsToGroupModalProps {
  groupId: number;
  groupName: string;
}

export const AddStudentsToGroupModal: React.FC<AddStudentsToGroupModalProps> = observer(
  ({ groupId, groupName }) => {
    const modalId = TeacherModals.AddStudentsToGroup;

    const { t } = useTranslation("groups");
    const { closeModal, isModalOpen } = useModal();
    const { getStudentTermByDomain } = useDomainHandler();
    const { setStudentsToAdd, addStudentsToGroup } = useGroups();

    const isModalCurrentlyOpen = isModalOpen(modalId);

    const formik = useFormik<AddStudentsToGroupFormFields>({
      enableReinitialize: true,
      initialValues: {
        studentEmails: [],
      },
      validationSchema: buildAddStudentsToGroupFormSchema(),
      validateOnChange: true,
      onSubmit: async (values) => {
        if (values.studentEmails && values.studentEmails.length > 0) {
          setStudentsToAdd(values.studentEmails.join(" "));
          await addStudentsToGroup(groupId);
        }

        // Reset form fields after submit is complete
        formik.resetForm();
        closeModal();
      },
    });

    return (
      <StyledModal
        dataCy={modalId}
        isOpen={isModalCurrentlyOpen}
        renderToElementType={ModalRenderType.REACT_PORTAL}
        onClose={closeModal}
      >
        <form noValidate onSubmit={formik.handleSubmit}>
          <CssFlex flexDirection="column" gap={1} padding="2rem">
            <Heading2>{t("groups:groupDetails.addStudentsToGroupModal.title")}</Heading2>
            <ParagraphLarge>
              <Trans
                components={{ groupNameTag: <b data-cy="target-group-name" /> }}
                i18nKey="groups:groupDetails.addStudentsToGroupModal.description"
                t={t}
                values={{
                  groupName,
                  studentTerm: getStudentTermByDomain({
                    usePlural: true,
                  }),
                }}
              />
            </ParagraphLarge>
            <Note variant="warning">
              <Trans
                i18nKey="groups:groupDetails.addStudentsToGroupModal.warning"
                t={t}
                values={{
                  studentTerm: getStudentTermByDomain({
                    usePlural: true,
                  }),
                }}
              />
            </Note>
            <MultipleEmailInput
              dataCy="group-student-multiple-email-input"
              onSelectedEmailsChanged={(emails) => {
                formik.setFieldValue(
                  "studentEmails",
                  emails.map(({ email }) => email),
                );
              }}
            />
            <CssFlex justifyContent="space-between" marginTop={2} gap={1}>
              <CssButton disabled={formik.isSubmitting} onClick={closeModal}>
                {t("groups:groupDetails.addStudentsToGroupModal.cancelButton")}
              </CssButton>
              <CssButton
                data-cy="confirm-add-students-button"
                variant="primary"
                disabled={!formik.dirty || !formik.isValid}
                isLoading={formik.isSubmitting}
                onClick={formik.submitForm}
              >
                {t("groups:groupDetails.addStudentsToGroupModal.confirmButton")}
              </CssButton>
            </CssFlex>
          </CssFlex>
        </form>
      </StyledModal>
    );
  },
);
